import {
  ExternalLink,
  Globe,
  Instagram,
  Mail,
  MessageCircle,
  Phone,
  Send,
  Youtube,
  type LucideIcon,
} from 'lucide-react';
import type { Language, VisitingCardButtonsId } from '../../types';
import { _t } from '../../lib/i18n';
import { tenantCatalogUrl } from '../../lib/tenantHost';
import { buildContactLinkActions } from '../../lib/tenantContactLinks.js';

export type VisitingCardContactAction = {
  id: string;
  href: string;
  labelKey: string;
  Icon: LucideIcon;
  primary?: boolean;
  external?: boolean;
  /** Non-lucide glyph when needed (facebook / tiktok / pinterest). */
  glyph?: 'facebook' | 'tiktok' | 'pinterest';
};

type ContactsShape = {
  phone: string;
  email: string;
  whatsapp: string;
  social?: Record<string, string> | null;
  linkPresence?: Record<string, { card?: boolean; footer?: boolean; floating?: boolean }> | null;
};

type VisitingCardContactButtonsProps = {
  lang: Language;
  template: VisitingCardButtonsId;
  contacts: ContactsShape;
  tenantSlug: string;
  showStorefrontLink: boolean;
  /** Alive Album: Catalog of Works address, or null before Activate. Omitted elsewhere. */
  storefrontHref?: string | null;
  /** Contact ids the photographer turned off. Missing ids stay visible. */
  hiddenContactIds?: string[];
  /** Orbit template centers photo here. */
  portraitUrl?: string | null;
  displayName?: string;
};

const ICON_BY_ID: Record<string, LucideIcon> = {
  phone: Phone,
  whatsapp: MessageCircle,
  email: Mail,
  website: ExternalLink,
  storefront: Globe,
  instagram: Instagram,
  youtube: Youtube,
  telegram: Send,
};

const GLYPH_BY_ID: Record<string, VisitingCardContactAction['glyph']> = {
  facebook: 'facebook',
  tiktok: 'tiktok',
  pinterest: 'pinterest',
};

function FacebookGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M14 8h3V4h-3c-2.2 0-4 1.8-4 4v2H8v4h2v8h4v-8h3l1-4h-4V8c0-.6.4-1 1-1z" />
    </svg>
  );
}

function TikTokGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.6 7.2a5.4 5.4 0 0 1-3.2-1.1v7.3a5.5 5.5 0 1 1-5.5-5.5c.3 0 .6 0 .9.1v2.7a2.8 2.8 0 1 0 2 2.7V2.5h2.6c.2 1.5 1.3 2.8 2.7 3.3v1.4z" />
    </svg>
  );
}

function PinterestGlyph({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2C6.5 2 2 6.5 2 12c0 4.1 2.5 7.6 6.1 9.1-.1-.8-.2-2 0-2.9.2-.8 1.3-5.4 1.3-5.4s-.3-.7-.3-1.6c0-1.5.9-2.6 2-2.6.9 0 1.4.7 1.4 1.5 0 .9-.6 2.3-.9 3.5-.3 1.1.5 1.9 1.5 1.9 1.8 0 3.2-1.9 3.2-4.7 0-2.4-1.8-4.2-4.3-4.2-2.9 0-4.6 2.2-4.6 4.4 0 .9.3 1.8.8 2.3.1.1.1.2.1.3l-.3 1.2c0 .2-.1.2-.3.1-1.3-.6-2.1-2.5-2.1-4 0-3.3 2.4-6.3 6.8-6.3 3.6 0 6.3 2.5 6.3 5.9 0 3.5-2.2 6.3-5.3 6.3-1 0-2-.5-2.3-1.2l-.6 2.4c-.2.9-.8 2-1.2 2.7 1 .3 2 .4 3.1.4 5.5 0 10-4.5 10-10S17.5 2 12 2z" />
    </svg>
  );
}

function ActionIcon({
  action,
  size,
}: {
  action: VisitingCardContactAction;
  size: number;
}) {
  if (action.glyph === 'facebook') return <FacebookGlyph size={size} />;
  if (action.glyph === 'tiktok') return <TikTokGlyph size={size} />;
  if (action.glyph === 'pinterest') return <PinterestGlyph size={size} />;
  const Icon = action.Icon;
  return <Icon size={size} />;
}

function labelKeyFor(id: string): string {
  if (id === 'storefront') return 'visitingCard.action.storefront';
  if (id === 'phone' || id === 'whatsapp' || id === 'email') {
    return `visitingCard.action.${id}`;
  }
  return `settings.contactLink_${id}`;
}

export function buildVisitingCardActions(options: {
  contacts: ContactsShape;
  tenantSlug: string;
  showStorefrontLink: boolean;
  storefrontHref?: string | null;
  hiddenContactIds?: string[];
}): VisitingCardContactAction[] {
  const { contacts, tenantSlug, showStorefrontLink, storefrontHref, hiddenContactIds = [] } = options;
  const hidden = new Set(hiddenContactIds);
  const built = buildContactLinkActions({
    phone: contacts.phone,
    email: contacts.email,
    whatsapp: contacts.whatsapp,
    social: contacts.social || undefined,
    presence: contacts.linkPresence || undefined,
    surface: 'card',
  });

  const actions: VisitingCardContactAction[] = built
    .filter((a) => !hidden.has(a.id))
    .map((a, index) => ({
    id: a.id,
    href: a.href,
    labelKey: labelKeyFor(a.id),
    Icon: ICON_BY_ID[a.id] || ExternalLink,
    glyph: GLYPH_BY_ID[a.id],
    primary: index === 0 && (a.id === 'whatsapp' || a.id === 'phone'),
    external: a.external,
  }));

  if (showStorefrontLink && tenantSlug) {
    const href = storefrontHref === undefined ? tenantCatalogUrl(tenantSlug) : storefrontHref;
    if (href) {
      actions.push({
        id: 'storefront',
        href,
        labelKey: 'visitingCard.action.storefront',
        Icon: Globe,
        external: true,
      });
    }
  }
  return actions;
}

export function VisitingCardContactButtons({
  lang,
  template,
  contacts,
  tenantSlug,
  showStorefrontLink,
  storefrontHref,
  hiddenContactIds = [],
  portraitUrl,
  displayName,
}: VisitingCardContactButtonsProps) {
  const actions = buildVisitingCardActions({
    contacts,
    tenantSlug,
    showStorefrontLink,
    storefrontHref,
    hiddenContactIds,
  });
  // News entry is only the floating FAB (VisitingCardNewsProvider), not a contact-row button.
  if (actions.length === 0) {
    return null;
  }

  const stopBubble = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
  };

  const linkProps = {
    onClick: stopBubble,
    onPointerDown: stopBubble,
    onTouchEnd: stopBubble,
  } as const;

  if (template === 'orbit') {
    return (
      <div
        data-no-flip
        className="relative mx-auto flex h-56 w-56 items-center justify-center"
        onClick={stopBubble}
        onPointerDown={stopBubble}
        onTouchEnd={stopBubble}
      >
        <div className="z-10 flex h-28 w-28 flex-col items-center justify-center overflow-hidden rounded-full bg-stone-200 shadow-md">
          {portraitUrl ? (
            <img src={portraitUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="px-2 text-center text-xs font-semibold text-stone-600">
              {displayName || '·'}
            </span>
          )}
        </div>
        {actions.slice(0, 6).map((action, index) => {
          const angle = (index / Math.min(actions.length, 6)) * Math.PI * 2 - Math.PI / 2;
          const x = Math.cos(angle) * 96;
          const y = Math.sin(angle) * 96;
          return (
            <a
              key={action.id}
              href={action.href}
              target={action.id === 'storefront' || action.external ? '_blank' : undefined}
              rel="noreferrer"
              className="absolute flex h-11 w-11 items-center justify-center rounded-full bg-[var(--accent-primary)] text-[var(--accent-fg)] shadow-md"
              style={{ transform: `translate(${x}px, ${y}px)` }}
              aria-label={_t(action.labelKey, lang)}
              title={_t(action.labelKey, lang)}
              {...linkProps}
            >
              <ActionIcon action={action} size={18} />
            </a>
          );
        })}
      </div>
    );
  }

  if (template === 'labeled_bars' || template === 'split_cta') {
    const primary = actions.filter((a) => a.primary);
    const rest = actions.filter((a) => !a.primary);
    const ordered = template === 'split_cta' ? [...primary, ...rest] : actions;
    return (
      <div
        data-no-flip
        className="flex w-full flex-col gap-2"
        onClick={stopBubble}
        onPointerDown={stopBubble}
        onTouchEnd={stopBubble}
      >
        {ordered.map((action, idx) => {
          const big = template === 'split_cta' ? idx < 2 : true;
          return (
            <a
              key={action.id}
              href={action.href}
              target={action.id === 'storefront' || action.external ? '_blank' : undefined}
              rel="noreferrer"
              className={[
                'inline-flex items-center justify-center gap-2 rounded-xl font-semibold',
                big
                  ? 'bg-[var(--accent-primary)] px-4 py-3 text-sm text-[var(--accent-fg)]'
                  : 'bg-stone-200 px-3 py-2 text-xs text-stone-800',
              ].join(' ')}
              {...linkProps}
            >
              <ActionIcon action={action} size={big ? 18 : 14} />
              {_t(action.labelKey, lang)}
            </a>
          );
        })}
      </div>
    );
  }

  if (template === 'dock') {
    return (
      <div
        data-no-flip
        className="flex w-full flex-wrap items-center justify-around gap-y-1 bg-[var(--accent-primary)] px-2 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-[var(--accent-fg)]"
        onClick={stopBubble}
        onPointerDown={stopBubble}
        onTouchEnd={stopBubble}
      >
        {actions.map((action) => (
          <a
            key={action.id}
            href={action.href}
            target={action.id === 'storefront' || action.external ? '_blank' : undefined}
            rel="noreferrer"
            className="flex flex-col items-center gap-1 px-2 py-1"
            aria-label={_t(action.labelKey, lang)}
            {...linkProps}
          >
            <ActionIcon action={action} size={20} />
            <span className="max-w-[4.5rem] truncate text-[9px] font-medium uppercase tracking-tight">
              {_t(action.labelKey, lang)}
            </span>
          </a>
        ))}
      </div>
    );
  }

  if (template === 'pill_row') {
    return (
      <div
        data-no-flip
        className="flex flex-wrap justify-center gap-2"
        onClick={stopBubble}
        onPointerDown={stopBubble}
        onTouchEnd={stopBubble}
      >
        {actions.map((action) => (
          <a
            key={action.id}
            href={action.href}
            target={action.id === 'storefront' || action.external ? '_blank' : undefined}
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent-primary)] px-3 py-2 text-xs font-semibold text-[var(--accent-fg)]"
            {...linkProps}
          >
            <ActionIcon action={action} size={14} />
            {_t(action.labelKey, lang)}
          </a>
        ))}
      </div>
    );
  }

  if (template === 'typewriter') {
    return (
      <div
        data-no-flip
        className="flex flex-wrap justify-center gap-3"
        onClick={stopBubble}
        onPointerDown={stopBubble}
        onTouchEnd={stopBubble}
      >
        {actions.map((action) => (
          <a
            key={action.id}
            href={action.href}
            target={action.id === 'storefront' || action.external ? '_blank' : undefined}
            rel="noreferrer"
            className="flex h-14 w-14 flex-col items-center justify-center rounded-full border border-[var(--accent-primary)]/35 bg-stone-50 text-[var(--accent-primary)] shadow-sm"
            aria-label={_t(action.labelKey, lang)}
            title={_t(action.labelKey, lang)}
            {...linkProps}
          >
            <ActionIcon action={action} size={18} />
          </a>
        ))}
      </div>
    );
  }

  if (template === 'stamp_grid') {
    return (
      <div
        data-no-flip
        className="grid grid-cols-3 gap-2"
        onClick={stopBubble}
        onPointerDown={stopBubble}
        onTouchEnd={stopBubble}
      >
        {actions.map((action) => (
          <a
            key={action.id}
            href={action.href}
            target={action.id === 'storefront' || action.external ? '_blank' : undefined}
            rel="noreferrer"
            className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-[var(--accent-primary)]/30 bg-white p-2 text-center text-[var(--accent-primary)]"
            {...linkProps}
          >
            <ActionIcon action={action} size={20} />
            <span className="text-[10px] font-semibold leading-tight">{_t(action.labelKey, lang)}</span>
          </a>
        ))}
      </div>
    );
  }

  // keypad (default fallback)
  return (
    <div
      data-no-flip
      className="grid grid-cols-3 gap-2"
      onClick={stopBubble}
      onPointerDown={stopBubble}
      onTouchEnd={stopBubble}
    >
      {actions.map((action) => (
        <a
          key={action.id}
          href={action.href}
          target={action.id === 'storefront' || action.external ? '_blank' : undefined}
          rel="noreferrer"
          className="flex aspect-square flex-col items-center justify-center gap-1 rounded-md bg-[var(--accent-primary)] text-[var(--accent-fg)]"
          aria-label={_t(action.labelKey, lang)}
          {...linkProps}
        >
          <ActionIcon action={action} size={22} />
          <span className="px-1 text-[9px] font-bold uppercase tracking-tighter">
            {_t(action.labelKey, lang)}
          </span>
        </a>
      ))}
    </div>
  );
}
